/**
 * Zuteilung fuer Experimente und Prozent-Rollouts. Reine Funktionen, kein
 * React, kein Netz.
 *
 * Bucket: md5(unitId + ':' + salt) → erste 7 Hex-Zeichen als Zahl → % 10000.
 * In SQL identisch nachrechenbar:
 *
 *   ('x' || substr(md5(unit_id || ':' || salt), 1, 7))::bit(28)::int % 10000
 *
 * Dieselbe Formel steckt in der Landingpage (aivy-dashboard-landingpage) und
 * im experimentEvaluator (aivy-backend-app). Die Testvektoren in
 * bucketing.test.js muessen in allen drei Repos dieselben Werte liefern.
 *
 * Rollout: bucket < percent * 100 → 'on'. Wer bei 10 % drin ist, bleibt es bei
 * 30 %. Experiment: Gewichte werden kumuliert und auf 10000 Buckets
 * umgerechnet. Die erste Variante ist immer Control.
 */
import { md5 } from './md5'

export const BUCKETS = 10000

export const STATUS = {
  DRAFT: 'draft',
  RUNNING: 'running',
  PAUSED: 'paused',
  CONCLUDED: 'concluded'
}

export const KIND = { ROLLOUT: 'rollout', EXPERIMENT: 'experiment' }

export const ROLLOUT_OFF = 'off'
export const ROLLOUT_ON = 'on'

export const bucketOf = (unitId, salt) =>
  parseInt(md5(`${unitId}:${salt}`).slice(0, 7), 16) % BUCKETS

export const variantKeys = (flag) =>
  flag?.kind === KIND.ROLLOUT
    ? [ROLLOUT_OFF, ROLLOUT_ON]
    : (flag?.variants || []).map((v) => v.key)

export const controlOf = (flag) => variantKeys(flag)[0] ?? null

const pickWeighted = (variants, bucket) => {
  const total = variants.reduce((sum, v) => sum + Math.max(0, v.weight || 0), 0)
  if (!total) return variants[0]?.key ?? null

  let threshold = 0
  for (const v of variants) {
    threshold += (Math.max(0, v.weight || 0) / total) * BUCKETS
    if (bucket < threshold) return v.key
  }

  return variants[variants.length - 1].key
}

const matchesList = (allowed, value) =>
  !Array.isArray(allowed) || allowed.length === 0 || allowed.includes(value)

/**
 * Targeting greift nur auf Felder, die gesetzt sind. Ein leeres oder fehlendes
 * Feld schraenkt nicht ein.
 */
export const matchesTargeting = (targeting, ctx = {}) => {
  if (!targeting) return true

  if (!matchesList(targeting.stages, ctx.stage)) return false
  if (!matchesList(targeting.partner_ids, ctx.partnerId)) return false
  if (!matchesList(targeting.lead_ampel, ctx.leadAmpel)) return false
  if (
    typeof targeting.is_trial === 'boolean' &&
    targeting.is_trial !== !!ctx.isTrial
  ) {
    return false
  }

  return true
}

export const unitIdFor = (flag, ctx = {}) => {
  if (flag?.unit === 'partner') return ctx.partnerId || null
  if (flag?.unit === 'user') return ctx.userId || null
  return ctx.visitorId || null
}

/**
 * @returns {{ variant: string|null, tracked: boolean, reason: string }}
 *   variant: null nur bei unbekanntem Flag oder draft. Sonst mindestens
 *   Control, damit Call-Sites nie zwischen null und Control unterscheiden
 *   muessen.
 *   tracked: true heisst, diese Zuteilung zaehlt als Exposure fuer die
 *   Auswertung. Nur bei laufendem Test und echter Zufallszuteilung.
 */
export const evaluateFlag = (flag, ctx = {}) => {
  if (!flag) return { variant: null, tracked: false, reason: 'unknown' }

  const keys = variantKeys(flag)
  const control = keys[0] ?? null

  if (flag.winner && keys.includes(flag.winner)) {
    return { variant: flag.winner, tracked: false, reason: 'winner' }
  }

  if (flag.status === STATUS.DRAFT || !flag.status) {
    return { variant: null, tracked: false, reason: 'draft' }
  }

  if (flag.status !== STATUS.RUNNING) {
    return { variant: control, tracked: false, reason: flag.status }
  }

  if (!matchesTargeting(flag.targeting, ctx)) {
    return { variant: control, tracked: false, reason: 'targeting' }
  }

  const unitId = unitIdFor(flag, ctx)
  if (!unitId) return { variant: control, tracked: false, reason: 'no_unit' }

  const override = flag.overrides?.[unitId]
  if (override && keys.includes(override)) {
    return { variant: override, tracked: false, reason: 'override' }
  }

  const bucket = bucketOf(unitId, flag.salt || flag.key)

  if (flag.kind === KIND.ROLLOUT) {
    const percent = Math.min(100, Math.max(0, Number(flag.percent) || 0))
    return {
      variant: bucket < percent * 100 ? ROLLOUT_ON : ROLLOUT_OFF,
      tracked: false,
      reason: 'rollout'
    }
  }

  return {
    variant: pickWeighted(flag.variants || [], bucket),
    tracked: true,
    reason: 'bucket'
  }
}
